import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  painel: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  conexao: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  cabecalhoConexao: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  total: {
    fontWeight: 600,
  },
  aviso: {
    color: theme.palette.type === "dark" ? "#ffb74d" : "#b26a00",
    fontSize: 13,
  },
  vazio: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: "#888",
  },
  filtros: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
  },
}));

/** Primeiro e último dia do mês corrente, em UTC (igual ao backend). */
const mesCorrente = () => {
  const agora = new Date();
  const ano = agora.getUTCFullYear();
  const mes = agora.getUTCMonth();

  return {
    from: new Date(Date.UTC(ano, mes, 1)).toISOString().slice(0, 10),
    to: new Date(Date.UTC(ano, mes + 1, 0)).toISOString().slice(0, 10),
  };
};

/**
 * Formata custo. `null` NÃO é zero: significa que a Meta não informou o valor
 * (WABA em linha de crédito compartilhada). Mostrar "0,00" aí faria a conexão
 * parecer gratuita e o cliente não seria cobrado.
 */
const formatarCusto = (valor, moeda) => {
  if (valor === null || valor === undefined) {
    return i18n.t("metaUsage.semCusto");
  }

  const numero = Number(valor);
  const texto = numero.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

  return moeda ? `${moeda} ${texto}` : texto;
};

const MetaUsage = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const padrao = useMemo(() => mesCorrente(), []);
  const [from, setFrom] = useState(padrao.from);
  const [to, setTo] = useState(padrao.to);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [coletando, setColetando] = useState(false);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/plugins/meta-signup/usage", {
        params: { from, to },
      });
      setDados(data);
    } catch (err) {
      setDados(null);
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, [from, to]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const coletarAgora = useCallback(async () => {
    setColetando(true);
    try {
      const { data } = await api.post("/plugins/meta-signup/usage/collect");

      // Sem conexão oficial não há o que enfileirar. O toast de sucesso aqui
      // seria mentira, e o de erro seria pior: não houve erro nenhum.
      if (data?.semConexoes) {
        return;
      }

      toast.success(i18n.t("metaUsage.coletaEnfileirada"));
    } catch (err) {
      toastError(err);
    } finally {
      setColetando(false);
    }
  }, []);

  const conexoes = dados?.conexoes || [];

  // `undefined` enquanto não carregou: só afirmamos "não tem conexão" depois
  // que o backend disse quantas são. Antes disso o botão continua habilitado.
  const semConexoes = dados?.conexoesOficiais === 0;

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          {i18n.t("metaUsage.title")} ({conexoes.length})
        </Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filtros}>
            <TextField
              label={i18n.t("metaUsage.from")}
              type="date"
              size="small"
              variant="outlined"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={i18n.t("metaUsage.to")}
              type="date"
              size="small"
              variant="outlined"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {(user?.super || user?.profile === "admin") && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<RefreshIcon />}
                disabled={coletando || semConexoes}
                onClick={coletarAgora}
              >
                {i18n.t("metaUsage.coletar")}
              </Button>
            )}
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.painel} variant="outlined">
        {carregando && (
          <div className={classes.vazio}>
            <CircularProgress size={24} />
          </div>
        )}

        {!carregando && conexoes.length === 0 && (
          <div className={classes.vazio}>
            {semConexoes
              ? i18n.t("metaUsage.semConexoes")
              : i18n.t("metaUsage.vazio")}
          </div>
        )}

        {!carregando &&
          conexoes.map((conexao) => (
            <Paper
              key={conexao.whatsappId}
              variant="outlined"
              className={classes.conexao}
            >
              <div className={classes.cabecalhoConexao}>
                <Typography variant="subtitle1">
                  {conexao.nome || `#${conexao.whatsappId}`}
                </Typography>
                <Typography variant="body2" className={classes.total}>
                  {i18n.t("metaUsage.totalVolume")}: {conexao.totalVolume} ·{" "}
                  {i18n.t("metaUsage.totalCusto")}:{" "}
                  {formatarCusto(conexao.totalCusto, conexao.currency)}
                </Typography>
              </div>

              {!conexao.custoCompleto && (
                <div className={classes.aviso}>
                  {i18n.t("metaUsage.custoParcial")}
                </div>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("metaUsage.categoria")}</TableCell>
                    <TableCell>{i18n.t("metaUsage.tipo")}</TableCell>
                    <TableCell align="right">
                      {i18n.t("metaUsage.volume")}
                    </TableCell>
                    <TableCell align="right">
                      {i18n.t("metaUsage.custo")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conexao.categorias.map((linha, indice) => (
                    <TableRow key={`${linha.pricingCategory}-${linha.pricingType}-${indice}`}>
                      <TableCell>{linha.pricingCategory || "-"}</TableCell>
                      <TableCell>{linha.pricingType || "-"}</TableCell>
                      <TableCell align="right">{linha.volume}</TableCell>
                      <TableCell align="right">
                        {formatarCusto(linha.custo, conexao.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}

        {!carregando && conexoes.length > 0 && (
          <div className={classes.aviso} style={{ marginTop: 8 }}>
            {i18n.t("metaUsage.rodape")}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default MetaUsage;
